import { api } from './client'
import type { ApiResponse } from './client'
import { API_BASE_URL } from '@/lib/config'

export interface Product {
    id: string
    sku: string
    barcode?: string
    name: string
    description?: string
    unit: string
    cost_price: number
    unit_price: number
    weight_value?: number
    weight_unit?: string
    product_type?: 'unit' | 'combo'
    is_active: boolean
    is_sellable: boolean
    // price_includes_material: giá bán đã gồm giá vật tư → bật nút tách/gộp khi lên đơn.
    price_includes_material?: boolean
    // material_price_per_unit: BE tính Σ(sale_price × BOM.quantity)/1 SP. Dùng cho
    // tách/gộp: giá_tách = unit_price − material_price_per_unit. 0 nếu không có vật tư giá.
    material_price_per_unit?: number
    created_at: string
    updated_at: string
    images?: ProductImage[]
    /** Tổng tồn kho (SUM quantity_available mọi lô). Chỉ có ở list `/products`;
     *  các endpoint khác (getById, barcode) trả 0. */
    stock_available?: number
}

export interface ProductImage {
    id: string
    product_id: string
    image_url: string
    sort_order: number
    is_primary: boolean
    created_at: string
}

export interface CreateProductPayload {
    sku: string
    name: string
    barcode?: string
    description?: string
    unit: string
    cost_price: number
    unit_price: number
    weight_value?: number
    weight_unit?: string
    is_active?: boolean
    is_sellable?: boolean
    price_includes_material?: boolean
}

export interface UpdateProductPayload {
    sku?: string
    name?: string
    barcode?: string
    description?: string
    unit?: string
    cost_price?: number
    unit_price?: number
    weight_value?: number
    weight_unit?: string
    is_active?: boolean
    is_sellable?: boolean
    price_includes_material?: boolean
}

/** Một ô tồn đã gộp theo (kho, vị trí, mức CK đánh dấu). Ô phẳng — FE tự xoay
 *  thành ma trận vị trí × chiết khấu. */
export interface ProductStockCell {
    warehouse_id: string
    warehouse_name: string
    /** null khi lô chưa được gán vị trí; tồn của nó vẫn tính vào total_available. */
    position_id: string | null
    position_code: string | null
    position_name: string | null
    /** Mức CK đánh dấu của các lô trong ô (0 = không đánh dấu). */
    discount_percent: number
    quantity: number
    lot_count: number
}

export interface ProductStockBreakdown {
    product_id: string
    unit: string
    total_available: number
    cells: ProductStockCell[]
}

export interface ProductImportResult {
    imported: number
    skipped: number
    errors: { row: number; sku: string; error: string }[]
}

export const productApi = {
    list: (params?: Record<string, string | number | boolean | undefined>) =>
        api.get<Product[]>('/products', { params }),
    getById: (id: string) => api.get<Product>(`/products/${id}`),
    getStockBreakdown: (id: string) =>
        api.get<ProductStockBreakdown>(`/products/${id}/stock-breakdown`),
    getByBarcode: (barcode: string) =>
        api.get<Product>(`/products/barcode/${encodeURIComponent(barcode)}`),
    create: (data: CreateProductPayload) =>
        api.post<Product>('/products', data),
    update: (id: string, data: UpdateProductPayload) =>
        api.put<Product>(`/products/${id}`, data),
    delete: (id: string) => api.delete(`/products/${id}`),
    uploadImage: async (
        productId: string,
        file: File,
    ): Promise<ApiResponse<ProductImage>> => {
        const formData = new FormData()
        formData.append('image', file)
        const res = await fetch(
            `${API_BASE_URL}/products/${productId}/images`,
            {
                method: 'POST',
                body: formData,
                credentials: 'include',
            },
        )
        const data = (await res.json()) as ApiResponse<ProductImage>
        if (!res.ok) {
            throw new Error(data.error?.message || 'Upload failed')
        }
        return data
    },
    deleteImage: (productId: string, imageId: string) =>
        api.delete(`/products/${productId}/images/${imageId}`),
    setPrimaryImage: (productId: string, imageId: string) =>
        api.put(`/products/${productId}/images/${imageId}/primary`),
    importExcel: (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post<ProductImportResult>('/products/import', formData)
    },
}
